import {
  computed,
  reactive,
  ref,
  type ComputedRef,
  type UnwrapNestedRefs,
} from 'vue';

import * as userApi from '../api/userApi';
import type { User, UserIntercomCredentials, UserType } from '../types';

import apiClient from '@/modules/ApiClient';

export interface UserStore {
  user: ComputedRef<User | null>;
  isLoggedIn: ComputedRef<boolean>;
  isValidUser: ComputedRef<boolean>;
  isTeamMember: ComputedRef<boolean>;
  setUser: (user: User | null) => void;
  patchUser: (partial: Partial<User>) => void;
  clearUser: () => void;
  getUserDetails: (userId: string) => Promise<User>;
  getUserIntercomCredentials: (accountId?: string) => Promise<UserIntercomCredentials>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

const currentUser = ref<User | null>(null);

const validUserTypes: UserType[] = ['internal', 'team_member'];

export function getCurrentUser(): User | null {
  return currentUser.value;
}

export function setCurrentUser(user: User | null): void {
  currentUser.value = user;
}

export function patchCurrentUser(partial: Partial<User>): void {
  if (currentUser.value) {
    currentUser.value = {
      ...currentUser.value,
      ...partial,
    };
  }
}

export function clearCurrentUser(): void {
  currentUser.value = null;
}

export function useUserService(): UnwrapNestedRefs<UserStore> {
  const user = computed(() => currentUser.value);
  const isLoggedIn = computed(() => currentUser.value != null);
  const isValidUser = computed(() => Boolean(
    user.value?.type && validUserTypes.includes(user.value.type),
  ));
  const isTeamMember = computed(() => user.value?.type === 'team_member');

  async function getUserDetails(userId: string): Promise<User> {
    const details = await userApi.fetchUserDetails({
      userId,
      deps: { apiClient },
    });

    if (!details) {
      throw new Error('Failed to fetch user details');
    }

    return details;
  }

  async function getUserIntercomCredentials(
    accountId: string = currentUser.value?.default?.account_id ?? '',
  ): Promise<UserIntercomCredentials> {
    if (!accountId) {
      throw new Error('Account ID is required');
    }

    return userApi.fetchIntercomCredentials({
      accountId,
      deps: { apiClient },
    });
  }

  async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await userApi.changePassword({
      oldPassword,
      newPassword,
      deps: { apiClient },
    });
  }

  return reactive({
    user,
    isLoggedIn,
    isValidUser,
    isTeamMember,
    setUser: setCurrentUser,
    patchUser: patchCurrentUser,
    clearUser: clearCurrentUser,
    getUserDetails,
    getUserIntercomCredentials,
    changePassword,
  });
}
